import { useState, useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";

const P = ({ children }: { children: ReactNode }) => (
  <p
    style={{
      fontSize: "15px",
      color: "var(--text-muted)",
      lineHeight: "1.8",
      marginBottom: "14px",
    }}
  >
    {children}
  </p>
);

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "what-we-collect", label: "What We Collect" },
  { id: "how-we-use", label: "How We Use Data" },
  { id: "sharing", label: "Sharing & Disclosure" },
  { id: "storage", label: "Storage & Security" },
  { id: "retention", label: "Data Retention" },
  { id: "cookies", label: "Cookies" },
  { id: "rights", label: "Your Rights" },
  { id: "children", label: "Children's Privacy" },
  { id: "third-party", label: "Third-Party Services" },
  { id: "dpdpa", label: "DPDPA Compliance" },
  { id: "changes", label: "Changes to Policy" },
  { id: "contact-privacy", label: "Contact" },
];

export default function PrivacyPage() {
  const [active, setActive] = useState("overview");

  useEffect(() => {
    const updateActiveSection = () => {
      const scrollTop =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      const scrollPosition = scrollTop + 180;
      let current = SECTIONS[0].id;

      for (const s of [...SECTIONS].reverse()) {
        const el = document.getElementById(s.id);
        if (!el) continue;

        const sectionTop = el.getBoundingClientRect().top + scrollTop;
        if (sectionTop <= scrollPosition) {
          current = s.id;
          break;
        }
      }

      setActive(current);
    };

    const syncAfterLayout = () => requestAnimationFrame(updateActiveSection);

    syncAfterLayout();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    document.addEventListener("scroll", updateActiveSection, {
      passive: true,
      capture: true,
    });
    window.addEventListener("hashchange", syncAfterLayout);
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      document.removeEventListener("scroll", updateActiveSection, {
        capture: true,
      });
      window.removeEventListener("hashchange", syncAfterLayout);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, []);

  const heading = (text: string) => (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "20px",
        fontWeight: "700",
        color: "var(--text-primary)",
        marginBottom: "12px",
        paddingTop: "8px",
      }}
    >
      {text}
    </h2>
  );

  return (
    <Layout>
      <section
        style={{ padding: "32px 0 48px", background: "var(--navy,#0F172A)" }}
      >
        <div className="container">
          <span
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "700",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--orange)",
              marginBottom: "12px",
            }}
          >
            LEGAL
          </span>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(28px,4vw,42px)",
              fontWeight: "700",
              color: "#fff",
              letterSpacing: "-0.02em",
              marginBottom: "16px",
            }}
          >
            Privacy Policy
          </h1>
          <p
            style={{
              fontSize: "15px",
              color: "rgba(255,255,255,0.6)",
              maxWidth: "560px",
              lineHeight: "1.7",
            }}
          >
            This policy explains what data we collect, why we collect it, and
            how we keep it safe. We&apos;ve written it to be readable, not just
            legally defensible.
          </p>
        </div>
      </section>

      <section
        style={{
          padding: "64px 0 96px",
          background: "var(--bg-cream,#F7F4EF)",
        }}
      >
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "220px 1fr",
              gap: "64px",
              alignItems: "start",
            }}
          >
            <aside style={{ position: "sticky", top: "88px" }}>
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: "12px",
                }}
              >
                ON THIS PAGE
              </p>
              <nav>
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActive(s.id);
                      document.getElementById(s.id)?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                    style={{
                      display: "block",
                      padding: "5px 0 5px 12px",
                      fontSize: "13px",
                      color:
                        active === s.id ? "var(--teal)" : "var(--text-muted)",
                      borderLeft: `2px solid ${active === s.id ? "var(--teal)" : "transparent"}`,
                      textDecoration: "none",
                      lineHeight: "1.5",
                      transition: "all 0.15s",
                    }}
                  >
                    {i + 1}. {s.label}
                  </a>
                ))}
              </nav>
            </aside>

            <div>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid rgba(15,23,42,0.08)",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  marginBottom: "32px",
                }}
              >
                <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                  Last updated: 13 May 2026
                </span>
              </div>
              <div
                style={{
                  background: "rgba(31,95,91,0.06)",
                  border: "1px solid rgba(31,95,91,0.15)",
                  borderRadius: "10px",
                  padding: "18px 22px",
                  marginBottom: "40px",
                }}
              >
                <P>
                  <strong>Short version:</strong> We collect only what we need
                  to run the platform. We do not sell your data. Your business
                  data is yours. We use Google Analytics for site usage. You can
                  request deletion at any time.
                </P>
              </div>

              <section id="overview">
                {heading("1. Overview")}
                <P>
                  Maltora (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a
                  software-as-a-service platform for beverage businesses,
                  operated by ZapCom Group. This Privacy Policy describes how we
                  collect, use, store, and share personal and business data when
                  you visit our website (maltora.app) or use our platform.
                </P>
                <P>
                  By using Maltora, you agree to the practices described in this
                  policy. If you do not agree, please discontinue use of the
                  platform.
                </P>
              </section>

              <section id="what-we-collect" style={{ marginTop: "40px" }}>
                {heading("2. What We Collect")}
                <P>
                  We collect information in three ways: information you provide
                  directly, information collected automatically, and information
                  from third parties.
                </P>
                <P>
                  <strong>Information you provide:</strong> Name, email address,
                  company name, job role, phone number (optional), and any
                  content you submit through forms or the platform.
                </P>
                <P>
                  <strong>Automatically collected:</strong> IP address, browser
                  type, pages visited, time on site, referral source, and device
                  information via cookies and analytics tools.
                </P>
                <P>
                  <strong>Platform data:</strong> Orders, inventory records,
                  payment data, buyer and seller profiles, and transaction
                  history you create or import into Maltora.
                </P>
              </section>

              <section id="how-we-use" style={{ marginTop: "40px" }}>
                {heading("3. How We Use Data")}
                <P>
                  We use your data to: provide and improve the Maltora platform;
                  respond to inquiries and support requests; send transactional
                  emails (order confirmations, invoices, alerts); send product
                  updates and announcements (you can opt out); analyse usage to
                  improve features; comply with legal obligations.
                </P>
                <P>
                  We do not use your data to train AI models, sell to
                  advertisers, or profile you for third-party marketing.
                </P>
              </section>

              <section id="sharing" style={{ marginTop: "40px" }}>
                {heading("4. Sharing & Disclosure")}
                <P>
                  We do not sell your personal data. We share data only in the
                  following circumstances:
                </P>
                <P>
                  <strong>Service providers:</strong> Infrastructure (AWS),
                  email delivery (Postmark), analytics (Google Analytics), and
                  payment processing (Razorpay). These providers are
                  contractually bound to protect your data.
                </P>
                <P>
                  <strong>Legal requirements:</strong> When required by law,
                  court order, or to protect the rights and safety of Maltora or
                  its users.
                </P>
                <P>
                  <strong>Business transfers:</strong> If Maltora is acquired or
                  merged, your data may be transferred as part of that
                  transaction. We will notify you before your data is
                  transferred and becomes subject to a different privacy policy.
                </P>
              </section>

              <section id="storage" style={{ marginTop: "40px" }}>
                {heading("5. Storage & Security")}
                <P>
                  Your data is stored on AWS servers located in India
                  (ap-south-1 region). We use AES-256 encryption at rest and TLS
                  1.2+ in transit. Access to production data is restricted to
                  authorised engineers and is logged.
                </P>
                <P>
                  No system is 100% secure. If you discover a security
                  vulnerability, please report it to{" "}
                  <a
                    href="mailto:support@maltora.app"
                    style={{ color: "var(--teal)" }}
                  >
                    support@maltora.app
                  </a>
                  .
                </P>
              </section>

              <section id="retention" style={{ marginTop: "40px" }}>
                {heading("6. Data Retention")}
                <P>
                  We retain your account data for as long as your account is
                  active, plus 90 days after deletion to allow recovery.
                  Transaction records (orders, invoices, payments) are retained
                  for 7 years to comply with Indian accounting regulations.
                  Analytics data is retained for 26 months.
                </P>
              </section>

              <section id="cookies" style={{ marginTop: "40px" }}>
                {heading("7. Cookies")}
                <P>
                  We use essential cookies (session management, security),
                  analytics cookies (Google Analytics — anonymised), and
                  preference cookies (language, UI settings). We do not use
                  advertising or third-party tracking cookies.
                </P>
                <P>
                  You can control cookies through your browser settings.
                  Disabling essential cookies may affect platform functionality.
                </P>
              </section>

              <section id="rights" style={{ marginTop: "40px" }}>
                {heading("8. Your Rights")}
                <P>
                  You have the right to: access the personal data we hold about
                  you; correct inaccurate data; request deletion of your data;
                  object to or restrict processing; receive your data in a
                  portable format; withdraw consent at any time.
                </P>
                <P>
                  To exercise any of these rights, email{" "}
                  <a
                    href="mailto:support@maltora.app"
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    support@maltora.app
                  </a>
                  . We will respond within 30 days.
                </P>
              </section>

              <section id="children" style={{ marginTop: "40px" }}>
                {heading("9. Children's Privacy")}
                <P>
                  Maltora is a B2B platform intended for business users aged 18
                  and above. We do not knowingly collect data from anyone under
                  18. If you believe we have inadvertently collected such data,
                  please contact us immediately.
                </P>
              </section>

              <section id="third-party" style={{ marginTop: "40px" }}>
                {heading("10. Third-Party Services")}
                <P>
                  Our platform integrates with third-party services including
                  Google Analytics, Razorpay, Postmark, and AWS. Each has its
                  own privacy policy. We are not responsible for the practices
                  of third-party services accessed through links on our site.
                </P>
              </section>

              <section id="dpdpa" style={{ marginTop: "40px" }}>
                {heading("11. DPDPA Compliance")}
                <P>
                  Maltora complies with India&apos;s Digital Personal Data
                  Protection Act, 2023 (DPDPA). We process personal data only
                  for lawful purposes, maintain records of processing
                  activities, and will notify affected users within 72 hours of
                  a confirmed data breach.
                </P>
                <P>
                  For DPDPA-specific inquiries, contact{" "}
                  <a
                    href="mailto:support@maltora.app"
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    support@maltora.app
                  </a>
                  .
                </P>
              </section>

              <section id="changes" style={{ marginTop: "40px" }}>
                {heading("12. Changes to Policy")}
                <P>
                  We may update this policy from time to time. We will notify
                  you of significant changes by email or via an in-app banner at
                  least 14 days before changes take effect. Continued use of the
                  platform after that date constitutes acceptance.
                </P>
              </section>

              <section id="contact-privacy" style={{ marginTop: "40px" }}>
                {heading("13. Contact")}
                <P>
                  For privacy-related questions:{" "}
                  <a
                    href="mailto:support@maltora.app"
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    support@maltora.app
                  </a>
                </P>
                <P>ZapCom Group · Bangalore, India</P>
                <P>
                  <Link
                    to="/terms"
                    state={{ fromApp: true }}
                    style={{ color: "var(--teal)" }}
                  >
                    Terms of Service
                  </Link>
                </P>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
